import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PrimaryButton from "../../../components/button/PrimaryButton";
import {
  createEmployee,
  getEmployee,
  updateEmployee,
} from "../../../services/employees";
import SectionTitle from "../../../components/section/SectionTitle";
import SectionHeader from "../../../components/section/SectionHeader";

const blankEmployee = {
  email: "",
  password: "",
  firstName: "",
  lastName: "",
  phoneNumber: "",
  address: "",
};

const EmployeeForm = () => {
  const { id } = useParams();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(blankEmployee);
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) return;
    getEmployee(id)
      .then((response) =>
        setEmployee({ ...blankEmployee, ...response, password: "" }),
      )
      .catch((error) => setErrors([error.message]));
  }, [editing, id]);

  const update = (field, value) =>
    setEmployee((current) => ({ ...current, [field]: value }));

  const submit = async () => {
    const nextErrors = [];

    if (!employee.email) nextErrors.push("Email is required.");

    if (!employee.firstName) nextErrors.push("First name is required.");

    if (!employee.lastName) nextErrors.push("Last name is required.");

    if (!editing && employee.password.length < 5)
      nextErrors.push("Password must be at least 5 characters.");

    if (nextErrors.length) return setErrors(nextErrors);

    setSaving(true);
    setErrors([]);

    const payload = {
      email: employee.email,
      firstName: employee.firstName,
      lastName: employee.lastName,
      phoneNumber: employee.phoneNumber || null,
      address: employee.address || null,
    };

    try {
      if (editing) await updateEmployee(id, payload);
      else
        await createEmployee({
          ...payload,
          password: employee.password,
        });

      navigate("/employees", { replace: true });
    } catch (error) {
      setErrors([error.message]);
    } finally {
      setSaving(false);
    }
  };

  const fields = [
    ["First Name", "firstName"],
    ["Last Name", "lastName"],
    ["Email", "email", "email"],
    ["Phone Number", "phoneNumber"],
    ["Address", "address"],
  ];

  return (
    <div className="flex-auto p-4 py-8 bg-neutral-light">
      <div className="pb-8">
        <SectionHeader>
          <SectionTitle title={editing ? "Edit Employee" : "Add Employee"} />
        </SectionHeader>
      </div>

      {errors.length ? (
        <div
          role="alert"
          className="bg-errorBg text-errorText text-sm p-4 mb-8 border-errorBorder border-[0.5px] rounded-sm"
        >
          <ul>
            {errors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {fields.map(([label, field, type = "text"]) => (
          <Input
            key={field}
            label={label}
            type={type}
            value={employee[field]}
            onChange={(value) => update(field, value)}
          />
        ))}

        {!editing ? (
          <Input
            label="Initial Password"
            type="password"
            value={employee.password}
            onChange={(value) => update("password", value)}
          />
        ) : null}
      </div>

      <div className="mt-8 flex justify-start gap-3">
        <PrimaryButton
          title={editing ? "Save Employee" : "Create Employee"}
          onClick={submit}
          isBusy={saving}
        />

        <button
          type="button"
          onClick={() => navigate("/employees")}
          className="rounded px-4 py-2 text-sm font-semibold text-slate-700"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const Input = ({ label, type = "text", value, onChange }) => (
  <div className="relative w-full mb-3">
    <label className="block uppercase text-xs font-bold mb-2">{label}</label>
    <input
      aria-label={label}
      type={type}
      value={value ?? ""}
      onChange={(event) => onChange(event.target.value)}
      className="border-0 px-3 py-3 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
    />
  </div>
);

export default EmployeeForm;
